<template>
    <div>
        <ModalConfirm :text="errorMessage" @confirm="processQuote" @close="closeConfirmModal" />
        <div class="container-fluid">
            <div class="row quote-page-body" style="padding: 20px">
                <div
                    id="quote_search"
                    class="mb-4 pb-4"
                    style="display: flex; justify-content: center"
                >
                    <QuoteFilter
                        ref="quoteFilter"
                        @reset-quotes="reset"
                        @search-quotes="searchQuotes"
                    />
                </div>

                <div class="mt-4 pt-4 d-flex justify-content-center" v-if="loading">
                    <div class="spinner-border text-danger" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>

                <div v-else>
                    <div>
                        <ResponsibleDataTable
                            ref="quotesTable"
                            :headers="quoteHeaders"
                            :data="quoteList"
                            :serverPaging="true"
                            :totalItems="totalCount"
                            :initialPage="currentPage"
                            :initialPageSize="itemsPerPage"
                            :searchDemoMode="isSearchDemo"
                            @selectionChanged="selectionChanged"
                            @page-changed="handlePageChange"
                            @page-size-changed="handlePageSizeChange"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="hasAnySelection" class="sticky-actions">
            <div class="sticky-actions__count">{{ selectedIds.length }} selected</div>
            <div class="sticky-actions__buttons">
                <button
                    v-if="isRetail"
                    class="btn btn-danger"
                    @click="assignToMe"
                    :disabled="assigning || !hasSingleSelection"
                >
                    Assign to me
                </button>
                <button
                    class="btn btn-danger"
                    @click="searchAgain(this.selectedIds)"
                    :disabled="!hasSingleSelection"
                >
                    Search Again
                </button>
                <button
                    class="btn btn-danger"
                    @click="viewResume(this.selectedIds)"
                    :disabled="!hasSingleSelection"
                >
                    View / Resume
                </button>
                <button
                    class="btn btn-danger"
                    @click="downloadQuote(this.selectedIds)"
                    :disabled="!hasAnySelection"
                    v-if="!isSearchDemo"
                >
                    Download
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { getDates } from '@/helpers/dates.js'
    import moment from 'moment'
    import ResponsibleDataTable from '@/components/shared/ResponsibleDataTable.vue'
    import QuoteFilter from '@/components/shared/QuoteFilter.vue'

    import config from '@/services/config'

    import { mapStores } from 'pinia'
    import { useQuoteListStore } from '@/stores/quoteListStore'
    import { useSessionStore } from '@/stores/sessionStore'
    import { useUserStore } from '@/stores/userStore'
    import { useQuoteFormStore } from '@/stores/quoteFormStore'
    import { useDropdownsStore } from '@/stores/dropdownsStore'

    import CSVService from '@/services/csv.service'
    import { toast } from 'vue3-toastify'

    import { Modal } from 'bootstrap'
    import ModalConfirm from '@/components/modals/ModalConfirm.vue'
    import { createKeyboardShortcuts } from '@/utils/keyboardShortcuts'
    import { getRouteShortcutRuntime } from '@/utils/shortcutRegistry'

    export default {
        name: 'MyListQuotes',
        inject: ['appShell'],
        components: {
            ResponsibleDataTable,
            QuoteFilter,
            ModalConfirm,
        },
        computed: {
            ...mapStores(
                useQuoteListStore,
                useSessionStore,
                useUserStore,
                useQuoteFormStore,
                useDropdownsStore
            ),
            quoteList() {
                return this.quoteListStore.quoteList
            },
            totalCount() {
                return this.quoteListStore.totalCount
            },
            currentPage() {
                return this.quoteListStore.currentPage
            },
            itemsPerPage() {
                return this.quoteListStore.itemsPerPage
            },
            isRetail() {
                return config.getSubdomain() === 'retail'
            },
            isSearchDemo() {
                return config.isSearchDemo?.() ?? false
            },
            selectedQuotes() {
                const selectedSet = new Set(this.selectedIds)
                return this.quoteList.filter((quote) => selectedSet.has(quote.id))
            },
            hasAnySelection() {
                return this.selectedIds.length > 0
            },
            hasSingleSelection() {
                return this.selectedIds.length === 1
            },
        },
        async mounted() {
            if (!config.isSearchDemo?.()) {
                await this.loadQuoteTypes()
            }
            this.appShell?.setTopNavbarProps?.({
                enableGlobalSearch: true,
            })
            this.appShell?.setTopbarHandlers?.(
                this.isSearchDemo
                    ? {}
                    : {
                          downloadQuote: () => this.downloadQuote(this.selectedIds),
                      }
            )
            this.appShell?.setSidebarHandlers?.({})
            this.appShell?.setSideBarProps?.({
                displayQuotebutton: false,
                isBpaNoquote: false,
            })
            const runtime = getRouteShortcutRuntime('my-quotes', {
                handlers: {
                    reset_filters: () => this.$refs.quoteFilter?.triggerResetShortcut?.(),
                    search_quotes: () => this.$refs.quoteFilter?.triggerSearchShortcut?.(),
                    search_again: () => this.searchAgain(this.selectedIds),
                    view_resume: () => this.viewResume(this.selectedIds),
                    download_quotes: () => this.downloadQuote(this.selectedIds),
                },
                predicates: {
                    has_single_selection_with_rows: () =>
                        this.quoteList.length > 0 && this.hasSingleSelection,
                    has_any_selection_with_rows: () =>
                        this.quoteList.length > 0 && this.hasAnySelection,
                },
            })
            this.shortcutManager = createKeyboardShortcuts({
                target: window,
                capture: true,
                blockCtrlMeta: true,
                bindings: runtime.bindings,
            })
            this.shortcutManager.start()
        },
        beforeUnmount() {
            this.shortcutManager?.stop?.()
            this.appShell?.resetLayout?.()
        },
        data() {
            return {
                loading: false,
                quoteArray: [],
                errorMessage: '',
                my_quotes: true,
                all_quotes: true,
                standard_quotes: true,
                part: '',
                sa_id: '',
                customer: '',
                customer_ref: '',
                date_range: 'today',
                range: [moment().toDate(), moment().toDate()],
                dashboard_permissions: ['myquotes_research'],
                is_staff: true,
                dateIntervalGroups: [
                    { value: 'today', label: 'Today' },
                    { value: 'week', label: 'Week' },
                    { value: 'this_month', label: 'This Month' },
                    { value: 'prev_month', label: 'Prev Month' },
                    { value: 'this_quarter', label: 'This Quarter' },
                    { value: 'prev_quarter', label: 'Prev Quarter' },
                    { value: 'this_year', label: 'This Year' },
                    { value: 'prev_year', label: 'Prev Year' },
                    { value: 'all', label: 'All' },
                ],
                quoteHeaders: {
                    id: 'ID',
                    customer: 'Customer',
                    customer_ref: 'Customer Ref',
                    due_date: 'Due Date',
                    part_number_list: 'Part Number List',
                    part_and_sub_list: 'Part and Sub List',
                    time_created: 'Time Created',
                    name: 'Name',
                    total_value: 'Total Value',
                    condition_codes: 'Condition Code',
                    actions: 'Actions',
                },
                customer_items: [
                    'Customer (optional)',
                    'All Customers',
                    'Aerospace Services International',
                    'Amentum Services, Inc',
                    'Amentum Services, LASSO',
                    'ASRC Federal',
                    'Avocet Aviation',
                    'BONEAL',
                    'CAE Aviation',
                    'Commuter Air Technology',
                    'Competition',
                    'Defense & Logistics Agency',
                    'Elbit Systems',
                    'FAA',
                    'FDH Defense Aftermarket',
                    'General Dynamics',
                    'Interior Business Center, AQD',
                    'JLS',
                    'L3 Technologies',
                    'M7 Aerospace',
                    'Meta Special Aerospace',
                    'Northrop Grumman',
                    'Northrop Grumman (SEMA)',
                    'PartsBase RFQ',
                    'PNNL/DOE',
                    'PROS6/US GOV',
                    'PROSV/US GOV',
                    'Science Applications International Corp',
                    'Sierra Nevada Corporation',
                    'Special Mission Aircraft Support Services',
                    'Stock Research',
                    'Triangle Aviation',
                    'Turbo Air',
                    'Unical Defense',
                    'USAF',
                    'Valair Aviation',
                    'Vertex Aerospace',
                    'VSE Aviation, Inc.',
                ],
                selectedIds: [],
                assigning: false,
                shortcutManager: null,
            }
        },
        methods: {
            getDates,
            async loadQuoteTypes() {
                try {
                    await this.dropdownsStore.fetchQuoteTypes()
                } catch (error) {
                    console.error('Error loading quote types:', error)
                }
            },
            searchAgain(id) {
                if (!this.hasSingleSelection) return
                id = id && id.length > 0 ? id[0] : null
                let quote = this.quoteList.filter((quote) => quote.id === id)
                quote = quote && quote.length > 0 ? quote[0] : null

                if (quote) {
                    const partList = quote.part_number_list.join(',')
                    this.$router.push({
                        name: 'dashboard',
                        query: {
                            part: partList,
                            type: quote.quote_type,
                            customer: quote.customer,
                            qtd: quote?.qtd ? quote.qtd : 2,
                        },
                    })
                    this.clearSelection()
                }
            },
            viewResume(id) {
                if (!this.hasSingleSelection) return
                id = id && id.length > 0 ? id[0] : null
                if (id) {
                    this.$router.push({
                        name: 'dashboard',
                        query: { search: id },
                    })
                    this.clearSelection()
                }
            },
            displaysConfirmModal() {
                const modal = new Modal(document.getElementById('confirmModal'))
                modal.show()
            },
            async downloadQuote(idArray) {
                this.errorMessage = ''

                idArray.forEach((id) => {
                    const quoteItem = this.quoteList.find((quote) => quote.id == id)

                    if (!quoteItem.quote_finished) {
                        this.errorMessage =
                            'One or more selected quotes are not finished yet. Would you like to continue the download?'
                    }

                    if (quoteItem) {
                        this.quoteArray.push(quoteItem)
                    }
                })

                if (this.errorMessage) {
                    this.displaysConfirmModal()
                    this.clearSelection()
                    return
                }

                this.processQuote()
                this.clearSelection()
            },
            closeConfirmModal() {
                this.quoteArray = []
                this.errorMessage = ''
            },
            async processQuote() {
                const date = moment().format('YYYY-MM-DD')

                for (const q of this.quoteArray) {
                    const quotes = await CSVService.getQuoteCSVBySearchId(q.id)

                    if (!quotes) {
                        console.error('No CSV data found for quote ID:', q.id)
                        continue
                    }

                    if (!Array.isArray(quotes) || quotes.length === 0) {
                        toast.error(
                            `No quote data available for CSV generation for quote ID: ${q.id}`,
                            {
                                position: 'top-right',
                                autoClose: 2000,
                            }
                        )
                        continue
                    }

                    const quoteMetadata =
                        quotes.find(
                            (quote) => quote?.customer_ref || quote?.customer || quote?.quote_type
                        ) || {}
                    const fileName = CSVService.buildQuoteDownloadFileName({
                        date,
                        quoteType: q.quote_type || quoteMetadata.quote_type,
                        customer: q.customer || quoteMetadata.customer,
                        customerReference: q.customer_ref || quoteMetadata.customer_ref,
                    })
                    const csvFormmated = await CSVService.generateQuoteCSV(quotes)
                    CSVService.downloadCSV(csvFormmated, [], fileName)
                }

                this.quoteArray = []
                this.errorMessage = ''
            },
            selectionChanged(selectedIds) {
                const arr = Object.values(selectedIds)
                this.selectedIds = arr
            },
            change_date(date_range) {
                let range = this.getDates(date_range)
                if (range) {
                    this.range = range
                }
            },
            reset() {
                // Reset page to 1 and clear quote list
                this.quoteListStore.setCurrentPage(1)
                this.quoteListStore.setQuoteList([])
                this.clearSelection()
            },
            async searchQuotes(data, { resetPage = true } = {}) {
                if (resetPage) {
                    this.quoteListStore.setCurrentPage(1)
                }
                this.loading = true
                this.quoteListStore.setQuoteList([])
                this.clearSelection()

                try {
                    const pageSize = this.quoteListStore.itemsPerPage
                    const offset = (this.quoteListStore.currentPage - 1) * pageSize
                    await this.quoteListStore.fetchQuoteList({
                        ...data,
                        limit: pageSize,
                        offset,
                        pageSize,
                    })
                } catch (error) {
                    console.error('Failed to search quotes', error)
                } finally {
                    this.loading = false
                }
            },
            async handlePageChange(page) {
                this.quoteListStore.setCurrentPage(page)
                await this.searchQuotes(this.quoteFormStore.formData, { resetPage: false })
            },
            async handlePageSizeChange(size) {
                this.quoteListStore.setItemsPerPage(size)
                this.quoteListStore.setCurrentPage(1)
                await this.searchQuotes(this.quoteFormStore.formData, { resetPage: false })
            },
            async assignToMe() {
                if (this.selectedQuotes.length !== 1) {
                    alert('\nPlease select only one row to assign.')
                    return
                }

                const searchAction = this.selectedQuotes[0]

                if (searchAction.name !== 'system') {
                    alert('\nThis quote is already assigned.\nPlease select a different quote.')
                    return
                }

                this.assigning = true
                try {
                    const res = await this.quoteListStore.assignQuoteToMe(searchAction.id)

                    if (res?.success) {
                        this.viewResume([searchAction.id])
                        this.clearSelection()
                        return
                    }

                    if (res?.status === 409) {
                        alert(`Failed to assign quote: ${res.error}`)
                    }

                    if (res?.error) {
                        alert(`Failed to assign quote: ${res.error}`)
                    }

                } catch (error) {
                    alert('An unexpected error occurred while assigning the quote.')
                } finally {
                    this.assigning = false
                }
            },
            clearSelection() {
                this.selectedIds = []
                this.$refs.quotesTable?.clearSelection?.()
            },
        },
    }
</script>

<style scoped>
    .quote-page-body {
        padding-bottom: 6rem;
    }

    .sticky-actions {
        position: fixed;
        right: 1.25rem;
        bottom: 1rem;
        z-index: 1030;
        display: flex;
        align-items: center;
        gap: 0.75rem;
        background: rgba(13, 15, 20, 0.95);
        border: 1px solid rgba(255, 255, 255, 0.14);
        border-radius: 14px;
        padding: 0.75rem;
        box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
        backdrop-filter: blur(4px);
    }

    .sticky-actions__count {
        color: #ffffff;
        font-size: 0.86rem;
        font-weight: 700;
        letter-spacing: 0.02em;
        white-space: nowrap;
        opacity: 0.95;
    }

    .sticky-actions__buttons {
        display: flex;
        gap: 0.5rem;
    }

    .sticky-actions__buttons .btn {
        white-space: nowrap;
    }

    @media (max-width: 991px) {
        .sticky-actions {
            left: 0.75rem;
            right: 0.75rem;
            bottom: 0.75rem;
            flex-direction: column;
            align-items: stretch;
            gap: 0.55rem;
            border-radius: 12px;
        }

        .sticky-actions__buttons {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.45rem;
        }

        .sticky-actions__buttons .btn {
            width: 100%;
        }

        .quote-page-body {
            padding-bottom: 8.5rem;
        }
    }
</style>
